import * as React from 'react';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { Institution, PaymentAnnualSummaryView, PaymentSummaryView } from 'cb-model';
import { BlockSequence, List, PageTitle, QueryMulti, Section, useCacheRecord } from 'gossamer-front-end';
import { selectUser } from 'gossamer-front-end';
import { InstitutionStripeConnectPanel } from './StripeConnectPanel';
import { CustomInfo } from 'cb-unified';
import { getFilter } from 'gossamer-unified';

export const InstitutionPaymentsSummary = (): React.JSX.Element => {
  const user = useSelector(selectUser);
  const institutionId = (user?.customInfo as CustomInfo | undefined)?.adminInstitutionId ?? null;
  const institutionRecord = useCacheRecord(
    Institution,
    typeof institutionId === 'number' ? String(institutionId) : null,
    typeof institutionId !== 'number',
  );

  return (
    <>
      <PageTitle title="Payments" subtitle="Ticket revenue and payment status for your events" />
      {typeof institutionId === 'number' ? (
        <Section title="Pay-outs for Paid Events" subtitle="Stripe Connect: Set up payouts to this institution">
          <InstitutionStripeConnectPanel institutionRow={institutionRecord?.data} />
        </Section>
      ) : null}
      <Section title="Pay-out Summary" subtitle="Total expected and actual pay-out amounts over the last 12 months">
        <QueryMulti
          entity={PaymentAnnualSummaryView}
          render={BlockSequence}
          queryOptions={{
            where: {
              and: [getFilter('institution_id', 'eq', institutionId)],
            },
          }}
          showSettingsControl={false}
          showItemCount={false}
          showPaginator="never"
        />
      </Section>
      <Section title="Pay-outs per Event" subtitle="Expected and actual pay-out amounts per event">
        <p>
          This list shows you the number of paid bookings per event, the total amount you will be paid (or have been
          paid), as of this moment, and the amounts that have been paid back to users due to cancelling their bookings
          or due to the event being cancelled.
        </p>
        <QueryMulti
          entity={PaymentSummaryView}
          render={BlockSequence}
          queryOptions={{
            where: {
              and: [getFilter('institution_id', 'eq', institutionId)],
            },
          }}
        />
      </Section>
      <p>
        If you upgraded from a personal account and had paid events, you can see the payment details related to them{' '}
        <Link to="../user-summary">here</Link>.
      </p>
    </>
  );
};
